/**
 * Presentation logic for the Business Units screen (f5.1).
 *
 * Everything here is pure, so the rules that shape the table and its four
 * toolbar selects are testable without rendering anything. Nothing in this
 * file decides what a caller may see or do: visibility is the server's answer,
 * and so are the status label, the currency label and the CM.
 *
 * **Every filter runs client-side**, over the set the server returned whole.
 * The collection is nine rows and takes no `q`, `group`, `currency`, `cm`,
 * `status`, `sort` or `order` parameter -- f4.1's argument applies a fortiori,
 * and f1.5 already carved the exception for *"a later resource with genuinely
 * small bounded data"*.
 */

import type { ActiveChildren, BusinessUnit } from '@/api/client';
import { ApiError } from '@/api/client';
import { ownerFor } from '@/routes/business-unit-groups/ownershipView';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

/** The status code the screen styles as "good". Anything else is neutral. */
const ACTIVE = 'ACTIVE';

/**
 * The delivery roles a Business Unit supports, in the order they are shown.
 *
 * Named here only to LABEL and ORDER the unit's owner columns, its CM filter
 * and its dialog pickers. The server refuses anything else with `409
 * DELIVERY_ROLE_LEVEL_MISMATCH`, and it is the registry -- not this array --
 * that decides what the assign dialog offers.
 *
 * **DM is held here AND on a Client.** A unit names the delivery manager who
 * answers for the unit as a whole; a client names its own where one differs.
 * Neither overrides the other -- f9.1 unions a walk-down from every node a
 * person is named on, so a unit-level DM already reaches the clients and
 * engagements beneath it. The source forces this: 8 of the 9 units name more
 * than one DM across the 70 populated engagement rows, so no single level
 * carries the data.
 *
 * **CM is Client Manager.** The prototype's create dialog glosses it
 * *"Commercial Manager"*; that phrase occurs exactly once in the whole
 * repository, and the glossary, the brief and f4.2's already-seeded
 * `delivery_role` row all say Client Manager. The seeded label wins, which is
 * why no string in this file restates it.
 */
export const UNIT_ROLE_CODES = ['CM', 'DM'] as const;

export type UnitRoleCode = (typeof UNIT_ROLE_CODES)[number];

/** The currency code the prototype styles differently from the rest. */
const EUR = 'EUR';

/**
 * The badge class for a status.
 *
 * Only the CLASS is derived from the code -- the LABEL always comes from the
 * server's `status.name`, so a status renamed in the lookup table renames
 * itself here with no frontend change (f1.3's rule, restated by f2.2).
 */
export function badgeClassFor(statusCode: string): string {
  return statusCode === ACTIVE ? 'badge pill-success' : 'badge pill-neutral';
}

/**
 * The badge class for a reporting currency.
 *
 * The prototype draws USD as a neutral `badge-chip` and EUR as `pill-info`,
 * which is a real distinction worth keeping: exactly one of the nine units
 * reports in EUR, and a reader scanning the column is looking for it. A third
 * currency an Admin inserts renders as the neutral chip, which is the honest
 * default -- and the LABEL is still the server's, so nothing here maps `EUR`
 * to "Euro".
 */
export function currencyClassFor(currencyCode: string): string {
  return currencyCode === EUR ? 'badge pill-info' : 'badge badge-chip';
}

export function isActive(unit: BusinessUnit): boolean {
  return unit.status.code === ACTIVE;
}

/**
 * Which column the table is sorted by. EVERY column the table renders is here
 * except the one holding the row actions, which has nothing to order by, and
 * the row actions, which has nothing to order by. `clients` joined it when
 * f6.1 delivered the count this feature deferred by name.
 */
export type SortColumn =
  | 'code'
  | 'name'
  | 'group'
  | 'currency'
  | 'CM'
  | 'DM'
  | 'clients'
  | 'status';

/** The default order: by the parent group, as the server already sent it. */
export const DEFAULT_SORT: SortState<SortColumn> = { column: 'group', direction: 'asc' };

/** What a column sorts by for one unit. */
export function sortValueOf(unit: BusinessUnit, column: SortColumn): SortValue {
  switch (column) {
    case 'code':
      return unit.code;
    case 'name':
      return unit.name;
    case 'group':
      // The group's code, then the unit's, which is the order the server
      // already returned the collection in -- so the default sort agrees with
      // the unsorted set rather than reshuffling it on first render.
      return `${unit.business_unit_group.code} ${unit.code}`;
    case 'currency':
      return unit.reporting_currency.code;
    case 'clients':
      // f6.1's delivered count. A number, so it sorts numerically rather than
      // as "10" before "2".
      return unit.client_count;
    case 'status':
      return unit.status.name;
    default:
      return ownerFor(unit, column)?.display_name ?? null;
  }
}

/** The four toolbar selects. An empty string means "all". */
export interface UnitFilters {
  groupId: string;
  currencyCode: string;
  cmUserId: string;
  statusCode: string;
}

export const NO_FILTERS: UnitFilters = {
  groupId: '',
  currencyCode: '',
  cmUserId: '',
  statusCode: '',
};

/** Whether any of the four selects is narrowing the list. */
export function isFiltered(filters: UnitFilters): boolean {
  return Object.values(filters).some((value) => value !== '');
}

/**
 * The prototype's subtitle tail -- *"8 reporting USD, 1 reporting EUR"* --
 * computed from the rows rather than typed.
 *
 * The prototype states that sentence as a fact; it is true of the legacy, but
 * this screen ships against an empty table and will show whatever the phase 8
 * import actually produces. Deriving it is what keeps the claim honest.
 */
export function currencySummary(units: readonly BusinessUnit[]): string {
  const counts = new Map<string, number>();
  for (const unit of units) {
    const code = unit.reporting_currency.code;
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([code, count]) => `${count} reporting ${code}`)
    .join(', ');
}

export interface TableView {
  search: string;
  filters: UnitFilters;
  sort: SortState<SortColumn>;
}

/**
 * Search across code, name AND alias, case-insensitively.
 *
 * The alias is in the haystack because the prototype's placeholder promises it
 * -- *"Search unit, code or Kimble alias"* -- and because it is the only way to
 * find the unit a feed spelling belongs to. An operator holding
 * `CU US Assets Management` from a Kimble banner should be able to type it and
 * land on `CU US Asset 04`.
 */
export function applySearch(
  units: readonly BusinessUnit[],
  search: string,
): BusinessUnit[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...units];
  return units.filter(
    (unit) =>
      unit.code.toLowerCase().includes(needle) ||
      unit.name.toLowerCase().includes(needle) ||
      unit.aliases.some((alias) => alias.toLowerCase().includes(needle)),
  );
}

/** The prototype's four selects, all client-side over the loaded set. */
export function applyFilters(
  units: readonly BusinessUnit[],
  filters: UnitFilters,
): BusinessUnit[] {
  return units.filter((unit) => {
    if (filters.groupId && unit.business_unit_group.id !== filters.groupId) return false;
    if (filters.currencyCode && unit.reporting_currency.code !== filters.currencyCode) {
      return false;
    }
    if (filters.statusCode && unit.status.code !== filters.statusCode) return false;
    if (filters.cmUserId && ownerFor(unit, 'CM')?.user_id !== filters.cmUserId) {
      return false;
    }
    return true;
  });
}

export function applySort(
  units: readonly BusinessUnit[],
  sort: SortState<SortColumn>,
): BusinessUnit[] {
  return sortRows(units, sort, sortValueOf);
}

export function visibleUnits(
  units: readonly BusinessUnit[],
  view: TableView,
): BusinessUnit[] {
  return applySort(applyFilters(applySearch(units, view.search), view.filters), view.sort);
}

/** The distinct parent groups present in the loaded set, for the select. */
export function groupOptions(
  units: readonly BusinessUnit[],
): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  for (const unit of units) byId.set(unit.business_unit_group.id, unit.business_unit_group.name);
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * The distinct currencies present in the loaded set, for the select.
 *
 * Read from the ROWS rather than from the lookup catalogue, for the reason
 * f4.2 gave about its owner filters: a filter offering values that match no
 * row is a worse control than one offering the values that do. The create
 * dialog is the opposite case and reads the catalogue, because there the
 * question is what an Admin may choose.
 */
export function currencyOptions(
  units: readonly BusinessUnit[],
): { code: string; name: string }[] {
  const byCode = new Map<string, string>();
  for (const unit of units) {
    byCode.set(unit.reporting_currency.code, unit.reporting_currency.name);
  }
  return [...byCode.entries()]
    .map(([code, name]) => ({ code, name }))
    .sort((left, right) => left.code.localeCompare(right.code));
}

/** The distinct statuses present, so the select never offers an empty filter. */
export function statusOptions(
  units: readonly BusinessUnit[],
): { code: string; name: string }[] {
  const byCode = new Map<string, string>();
  for (const unit of units) byCode.set(unit.status.code, unit.status.name);
  return [...byCode.entries()]
    .map(([code, name]) => ({ code, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * The children a refused deactivation named, or null.
 *
 * The client never computes this: it issues the deactivation WITHOUT
 * acknowledgement first and renders whatever the server refused with. A
 * Business Unit has no registered children until f6.1 adds `Client`, so today
 * this is always null -- and when f6.1 lands, the dialog starts listing
 * clients with **no change to this file**.
 */
export function activeChildrenFrom(error: unknown): ActiveChildren | null {
  return error instanceof ApiError ? error.activeChildren : null;
}

/** Whether a failure is a refusal by policy rather than something to retry. */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Re-exported, not redefined: reading f2.1's error envelope is the whole API's
 * concern, and the provenance formatters are shared with the level above.
 */
export {
  PROVENANCE_MISSING,
  formatActor,
  formatMoment,
  initialsOf,
} from '@/routes/business-unit-groups/groupView';
export { generalMessage, problemFor } from '@/api/fieldErrors';
